import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
  Checkbox,
  Label,
  InputErrorMessage,
  MultiEmailInput,
  NumberInput,
} from 'shared/components';
import { platformFeatures } from 'shared/constants';
import { isFeatureEnabled, isFeatureAvailable } from 'shared/helpers';
import { useTranslation } from 'react-i18next';

const LicenseOptionsSection = ({
  companyID,
  maxActivations,
  maxActivationsError,
  allowUnlimitedActivations,
  handleAllowUnlimitedActivations,
  handleMaxActivationsChange,
  isUserBasedProduct,
  licenseQuantity,
  licenseQuantityError,
  handleLicenseQuantityChange,
  licenseUsers,
  handleLicenseUsersChange,
  preventVm,
  handlePreventVmChange,
  preventMaxTransfers,
  setPreventMaxTransfers,
  hasMaxTransfers,
  maxTransfersError,
  handleHasMaxTransfersChange,
  maxTransfers,
  handleMaxTransfersChange,
  isFloatingCloud,
  maxLicenseUsers,
  maxLicenseUsersError,
  handleMaxLicenseUsers,
  unlimitedMaxLicenseUsers,
  handleUnlimitedMaxLicenseUsers,
  userMaxActivations,
  userMaxActivationsError,
  handleUserMaxActivationsChange,
  allowOfflineActivation,
  handleAllowOfflineActivationChange,
}) => {
  const { t } = useTranslation();
  const companyFeatures = useSelector((state) =>
    get(state, 'user.company.features', []),
  );
  const featureMaxActivations = companyFeatures.find(
    (f) => f.key === 'extra:max-activations',
  );
  const maxActivationsAllowed = get(featureMaxActivations, 'value', 1);
  const isUnlimitedActivations = maxActivationsAllowed === -1;
  const maxActivationsAllowedText = isUnlimitedActivations
    ? '2147483647'
    : maxActivationsAllowed;

  const notAvailableMessage = (
    <>
      {t(
        'Virtual machine detection and management is not available in your plan.',
      )}
      &nbsp;
      {t('Contact sales to support VM prevention.')}
    </>
  );
  const notAvailableMaxTransfersMessage = (
    <>
      {t('Device transfer limits are not available in your plan.')}
      &nbsp;
      {t('Contact sales to support device transfer limit.')}
    </>
  );

  return (
    <section className="LicenseOptionsSection">
      <div className="section-header">{t('License options')}</div>
      {isFeatureEnabled(platformFeatures.extra_max_activations) && (
        <div className="section-row initial-checkbox">
          <Label text={t('Max Activations')} inputId="max-activations-input" />
          <NumberInput
            handleChange={handleMaxActivationsChange}
            value={maxActivations}
            error={maxActivationsError}
            min="1"
            max={maxActivationsAllowedText}
            id="max-activations-input"
            disabled={allowUnlimitedActivations}
          />
          {isUnlimitedActivations && (
            <Checkbox
              label={t('Allow unlimited activations')}
              inputId="allow-unlimited-activations-checkbox"
              checked={allowUnlimitedActivations}
              handleChange={handleAllowUnlimitedActivations}
            />
          )}
          <div>
            <Checkbox
              label={t('Allow offline activation')}
              inputId="allow-offline-activation-checkbox"
              checked={allowOfflineActivation}
              handleChange={handleAllowOfflineActivationChange}
            />
          </div>
        </div>
      )}
      <div className="section-row">
        <Checkbox
          label={t('Prevent device transfers')}
          inputId="prevent-transfers-checkbox"
          checked={preventMaxTransfers}
          handleChange={(val) => setPreventMaxTransfers(val)}
          featureEnabled={isFeatureEnabled(
            platformFeatures.extra_max_transfers,
          )}
          featureAvailable={isFeatureAvailable(
            platformFeatures.extra_max_transfers,
          )}
          notAvailableMessage={notAvailableMaxTransfersMessage}
        />
      </div>
      <div className="section-row">
        <Checkbox
          label={t('Has device transfer limit')}
          inputId="max-transfers-checkbox"
          checked={hasMaxTransfers}
          handleChange={handleHasMaxTransfersChange}
          featureEnabled={isFeatureEnabled(
            platformFeatures.extra_max_transfers,
          )}
          featureAvailable={isFeatureAvailable(
            platformFeatures.extra_max_transfers,
          )}
          notAvailableMessage={notAvailableMaxTransfersMessage}
        />
        {!!hasMaxTransfers && (
          <NumberInput
            handleChange={handleMaxTransfersChange}
            value={maxTransfers}
            error={maxTransfersError}
            min="1"
            max="2147483647"
            id="max-transfers-input"
            placeholder={t('Device transfer limit')}
          />
        )}
      </div>
      {!isUserBasedProduct && (
        <div className="section-row">
          <Label
            text={t('Number of license keys')}
            inputId="license-num-input"
          />
          <NumberInput
            handleChange={handleLicenseQuantityChange}
            value={licenseQuantity}
            error={licenseQuantityError}
            min="1"
            max="1000"
            id="license-num-input"
          />
        </div>
      )}
      {isUserBasedProduct && (
        <div className="max-users">
          <div className="section-row row-max-license-users">
            <Label text={t('Max license users')} />
            <NumberInput
              handleChange={handleMaxLicenseUsers}
              value={maxLicenseUsers}
              error={maxLicenseUsersError}
              min="0"
              max="2147483647"
              disabled={unlimitedMaxLicenseUsers}
            />
          </div>
          <div className="section-row row-max-license-users-checkbox">
            <Checkbox
              label={t('Unlimited users')}
              description={t('Only when is floating cloud')}
              inputId="max-license-users-checkbox"
              checked={unlimitedMaxLicenseUsers}
              handleChange={handleUnlimitedMaxLicenseUsers}
              disabled={!isFloatingCloud}
            />
          </div>
        </div>
      )}
      {isUserBasedProduct && (
        <div className="section-row">
          <Label
            text={t('Users')}
            inputId="multi-email-input"
            description={t(
              'Type or paste email addresses and press `Enter`...',
            )}
          />
          <MultiEmailInput
            emails={get(licenseUsers, 'emails', [])}
            onEmailSubmit={handleLicenseUsersChange}
            id="multi-email-input"
          />
          <InputErrorMessage text={get(licenseUsers, 'validationError', '')} />
        </div>
      )}
      {isUserBasedProduct && (
        <div className="section-row">
          <Label
            text={t('Max activations per user')}
            inputId="user-max-activations-input"
          />
          <NumberInput
            handleChange={handleUserMaxActivationsChange}
            value={userMaxActivations}
            error={userMaxActivationsError}
            min="1"
            max="2147483647"
            id="user-max-activations-input"
          />
        </div>
      )}
      <div className="section-row">
        <Checkbox
          label={t('Prevent virtual machine')}
          inputId="product-prevent-vm"
          checked={preventVm}
          handleChange={handlePreventVmChange}
          featureEnabled={isFeatureEnabled(platformFeatures.extra_prevent_vm)}
          featureAvailable={isFeatureAvailable(
            platformFeatures.extra_prevent_vm,
          )}
          notAvailableMessage={notAvailableMessage}
        />
      </div>
    </section>
  );
};

LicenseOptionsSection.propTypes = {
  companyID: PropTypes.number.isRequired,
  isUserBasedProduct: PropTypes.bool,
  maxActivations: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxActivationsError: PropTypes.string,
  allowUnlimitedActivations: PropTypes.bool,
  handleAllowUnlimitedActivations: PropTypes.func.isRequired,
  handleMaxActivationsChange: PropTypes.func.isRequired,
  licenseQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  licenseQuantityError: PropTypes.string,
  handleLicenseQuantityChange: PropTypes.func.isRequired,
  licenseUsers: PropTypes.object.isRequired,
  handleLicenseUsersChange: PropTypes.func.isRequired,
  userMaxActivations: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  userMaxActivationsError: PropTypes.string,
  handleUserMaxActivationsChange: PropTypes.func.isRequired,
  preventVm: PropTypes.bool,
  handlePreventVmChange: PropTypes.func.isRequired,
  preventMaxTransfers: PropTypes.bool,
  setPreventMaxTransfers: PropTypes.func.isRequired,
  hasMaxTransfers: PropTypes.bool,
  handleHasMaxTransfersChange: PropTypes.func.isRequired,
  maxTransfers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxTransfersError: PropTypes.string,
  handleMaxTransfersChange: PropTypes.func.isRequired,
  isFloatingCloud: PropTypes.bool,
  maxLicenseUsers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxLicenseUsersError: PropTypes.string,
  handleMaxLicenseUsers: PropTypes.func.isRequired,
  unlimitedMaxLicenseUsers: PropTypes.bool,
  handleUnlimitedMaxLicenseUsers: PropTypes.func.isRequired,
  allowOfflineActivation: PropTypes.bool,
  handleAllowOfflineActivationChange: PropTypes.func.isRequired,
};

LicenseOptionsSection.defaultProps = {
  maxActivations: '',
  maxActivationsError: '',
  allowUnlimitedActivations: false,
  isUserBasedProduct: false,
  licenseQuantity: '',
  licenseQuantityError: '',
  userMaxActivations: '',
  userMaxActivationsError: '',
  preventVm: false,
  preventMaxTransfers: false,
  hasMaxTransfers: false,
  maxTransfers: 0,
  maxTransfersError: '',
  isFloatingCloud: false,
  maxLicenseUsers: '',
  maxLicenseUsersError: '',
  unlimitedMaxLicenseUsers: false,
  allowOfflineActivation: false,
};

export default LicenseOptionsSection;
